<template>
  <div>
    <div
      :class="{
        'checklist-entry': true,
        flexrow: true,
        checked: entry.checked
      }"
      :key="'comment-checklist-' + '-' + index"
      v-for="(entry, index) in checklist"
    >
      <span class="flexrow-item" @click="toggleEntryChecked(entry)">
        <check-square-icon class="icon" v-if="entry.checked" />
        <square-icon class="icon" v-else />
      </span>
      <p
        v-if="timecodeName"
        class="checklist-text flexrow-item"
        v-html="
          renderComment(
            entry.text,
            mentions,
            department_mentions,
            personMap,
            departmentMap,
            timecodeName
          )
        "
      ></p>
      <at-ta
        :members="atOptions"
        name-key="full_name"
        :limit="2"
        @input="input => {onTextChanged(input, entry)}"
        v-else
      >
        <template slot="item" slot-scope="team">
          <template v-if="team.item.isTime"> ⏱️ frame </template>
          <template v-else-if="team.item.isDepartment">
            <span
              class="mr05"
              :style="{
                background: team.item.color,
                width: '10px',
                height: '10px',
                'border-radius': '50%'
              }"
            >
              &nbsp;
            </span>
            {{ team.item.full_name }}
          </template>
          <template v-else>
            <div class="flexrow">
              <people-avatar
                class="flexrow-item"
                :person="team.item"
                :size="20"
                :font-size="11"
                :no-cache="true"
                :is-link="false"
              />
              <span class="flexrow-item">
                {{ team.item.full_name }}
              </span>
            </div>
          </template>
        </template>
        <textarea-autosize
          type="text"
          class="checklist-text flexrow-item"
          :ref="`checklist-entry-${index}`"
          rows="1"
          :placeholder="$t('comments.task_placeholder')"
          @keypress.enter.prevent.native="addChecklistEntry(index, $event)"
          @keyup.backspace.native="removeChecklistEntry(index)"
          @keyup.up.native="focusPrevious(index)"
          @keyup.down.native="focusNext(index)"
          :disabled="entry.text.length !== 0 && disabled"
          v-model.trim="entry.text"
        />
      </at-ta>
    </div>
  </div>
</template>

<script>
import { CheckSquareIcon, SquareIcon } from 'vue-feather-icons'
import { mapGetters } from 'vuex'
import { renderComment, replaceTimeWithTimecode } from '@/lib/render'
import PeopleAvatar from '@/components/widgets/PeopleAvatar'
import AtTa from 'vue-at/dist/vue-at-textarea'

export default {
  name: 'checklist',

  components: {
    AtTa,
    PeopleAvatar,
    CheckSquareIcon,
    SquareIcon
  },

  data() {
    return {
      atOptions: [],
    }
  },

  props: {
    checklist: {
      default: () => [],
      type: Array
    },
    timecodeName: {
      default: null,
      type: String
    },
    team: {
      type: Array,
      default: () => []
    },
    mentions: {
      default: () => [],
      type: Array
    },
    department_mentions: {
      default: () => [],
      type: Array
    },
    fps: {
      type: Number,
      default: 25
    },
    revision: {
      type: Number,
      default: 1
    },
    time: {
      type: Number,
      default: 0
    },
    disabled: {
      default: false,
      type: Boolean
    }
  },

  computed: {
    ...mapGetters([
      'departmentMap',
      'personMap',
      'isCurrentUserClient',
      'productionDepartmentIds',
    ])
  },

  methods: {
    renderComment,

    onTextChanged(input, entry) {
      if (input.indexOf('@frame') >= 0) {
        this.$nextTick(() => {
          const text = replaceTimeWithTimecode(
            entry.text,
            this.revision,
            this.time,
            this.fps
          )
          entry.text = text
        })
      }
    },
    
    addChecklistEntry(index, event) {
      if (index === -1 || index === this.checklist.length - 1) {
        this.$emit('add-item', {
          index,
          text: '',
          checked: false
        })
      }

      this.$nextTick(() => {
        this.focusNext(index)
      })
    },

    removeChecklistEntry(index) {
      const entry = this.checklist[index]
      if (entry.text.length === 0) {
        this.$emit('remove-task', entry)
        this.focusPrevious(index)
      }
    },

    focusPrevious(index) {
      if (this.checklist.length > 0) {
        if (index === 0) index = this.checklist.length
        index--
        const entryRef = `checklist-entry-${index}`
        this.$refs[entryRef][0].$el.focus()
      }
    },

    focusNext(index) {
      if (this.checklist.length > 0) {
        if (index === this.checklist.length - 1) index = -1
        index++
        const entryRef = `checklist-entry-${index}`
        this.$refs[entryRef][0].$el.focus()
      }
    },

    toggleEntryChecked(entry) {
      entry.checked = !entry.checked
      this.$emit('emit-change')
    },

    timeCodeClicked(event) {
      this.$emit('time-code-clicked', event)
    },
  },

  mounted() {
    Array.from(document.getElementsByClassName(this.timecodeName)).forEach(
      element => {
        element.addEventListener('click', this.timeCodeClicked)
      }
    )
  },

  destroyed() {
    Array.from(document.getElementsByClassName(this.timecodeName)).forEach(
      element => {
        element.removeEventListener('click', this.timeCodeClicked)
      }
    )
  },

  watch: {
    team: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isCurrentUserClient) {
          this.atOptions = [
            ...this.team.filter(person =>
              ['admin', 'manager', 'supervisor', 'client'].includes(person.role)
            )
          ]
        } else {
          this.atOptions = [...this.team]
        }
        this.atOptions = this.atOptions.concat(
          this.productionDepartmentIds.map(departmentId => {
            const department = this.departmentMap.get(departmentId)
            return {
              isDepartment: true,
              full_name: department.name,
              color: department.color,
              id: departmentId
            }
          })
        )
        this.atOptions.push({
          isTime: true,
          full_name: 'frame'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dark {
  .checklist-entry {
    .checklist-text {
      color: $light-grey-light;
      background: transparent;

      &:active,
      &:focus,
      &:hover {
        background: $dark-grey;
        border: 1px solid $dark-grey-strong;
      }

      &:disabled {
        background: transparent;
        color: white;

        &:hover {
          border: 1px solid transparent;
        }
      }
    }

    &.checked .checklist-text {
      color: #999;
    }
  }
}

.checklist-entry {
  color: $grey;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 0.3em;

  .checklist-text {
    font-size: 0.9em;
    padding: 0.2em;
    padding-top: 0em;
    margin-right: 0.5em;
    margin-top: 4px;
    width: 100%;
    min-height: 20px;
    border: 1px solid transparent;

    &:focus,
    &:active,
    &:hover {
      border: 1px solid $light-grey;
    }

    &:disabled {
      background-color: white;
      color: #333;

      &:hover {
        border: 1px solid transparent;
      }
    }
  }

  &.checked .checklist-text {
    color: #BBB;
    text-decoration: line-through;
  }

  span {
    cursor: pointer;
    padding: 0.2em 0 0 0;
    margin-right: 0.2em;
    margin-left: 0;

    .icon {
      width: 20px;
    }
  }
}
</style>
